import * as React from 'react';
import { RefCallback, useMemo, useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { CalendarIcon, ClearIcon } from '@mui/x-date-pickers/icons';
import { IconButton, InputAdornment } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { IInputDefaultProps } from './i-input-default-props';

type Views = 'year' | 'month' | 'day' | 'hours' | 'minutes' | 'seconds';

interface EnhancedDatePickerProps extends IInputDefaultProps {
    onChange: (date: string | null) => void;
    value: string | null;
    views: readonly Views[];
    // `mask` is kept for call-site compatibility but no longer used: v6+ derives
    // the input mask from `format`, so callers may keep passing it as a no-op.
    mask?: string;
    inputFormat: string;
    localFormat?: string;
    disabled?: boolean;
    inputRef?: RefCallback<any>;
    clearable?: boolean;
}

export function DDatePicker(props: EnhancedDatePickerProps) {
    const {
        onChange,
        disabled = false,
        label,
        value,
        views,
        inputFormat,
        localFormat,
        helperText,
        error,
        disableGutters = false,
        inputRef,
        clearable = true,
    } = props;

    // Popup-only editing: the field is read-only (no keystroke/arrow editing);
    // the value is changed exclusively through the calendar popup, which we open
    // by clicking the field. So we control the open state ourselves.
    const [open, setOpen] = useState(false);

    // MUI only computes its own "today" reference once, on mount, so a picker
    // left blank on a long-lived form opens to whatever time the page loaded
    // rather than now. Re-snapshot it on every open so the calendar/clock
    // selector always starts at the current moment. This only affects where
    // the view starts when `value` is null — it never overrides a value the
    // user (or a prefill) has already set.
    const [referenceDate, setReferenceDate] = useState<Dayjs>(() => dayjs());

    // The external contract is string in / string out (formatted per
    // `inputFormat`). Internally the v7 picker speaks Dayjs, so parse on the way
    // in and re-serialize on the way out. The *displayed* sections follow
    // `localFormat` (e.g. month-name) when given, while the stored value always
    // round-trips through `inputFormat` — they're independent because the value
    // is the Dayjs object, not its rendering.
    const dayjsValue = useMemo<Dayjs | null>(
        () => (value ? dayjs(value, inputFormat) : null),
        [value, inputFormat],
    );

    return (
        <DateTimePicker
            views={views as Views[]}
            format={localFormat || inputFormat}
            value={dayjsValue}
            referenceDate={referenceDate}
            disabled={disabled}
            label={label}
            // Digital clock defaults to 5-minute (and 5-second) steps; show
            // every value 0–59 instead.
            timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
            // Controlled popup: clicking the field (input onMouseDown) or our
            // calendar button toggles `open` ourselves; `onClose` keeps state in
            // sync when the picker closes itself (click-away / selection).
            open={open}
            onOpen={() => {
                setReferenceDate(dayjs());
                setOpen(true);
            }}
            onClose={() => setOpen(false)}
            onChange={(newValue) => {
                if (newValue === null) {
                    // Cleared via the inline ✕.
                    onChange(null);
                } else if (dayjs.isDayjs(newValue) && newValue.isValid()) {
                    onChange(newValue.format(inputFormat));
                }
            }}
            slotProps={{
                // `field.readOnly` makes the section field fully non-editable:
                // it blocks all keystroke/arrow section editing (see
                // `useField`'s `handleContainerKeyDown`), so the value can only
                // change through the calendar popup. MUI ties its built-in
                // `clearable` ✕ to `!readOnly`, so that inline ✕ can never
                // render here — we provide our own clear button in the end
                // adornment below instead.
                field: {
                    readOnly: true,
                } as any,
                popper: {
                    className: 'DDatePicker',
                    placement: 'bottom-start',
                },
                textField: {
                    inputRef,
                    color: 'primary',
                    margin: 'dense',
                    size: 'small',
                    error,
                    helperText,
                    InputProps: {
                        // Replace the picker's default end adornment (calendar
                        // toggle only) with our own: an always-visible clear (✕)
                        // button — styled like the Autocomplete clear and shown
                        // even on empty/required fields — followed by the
                        // calendar toggle. We must re-render the calendar button
                        // ourselves because overriding `endAdornment` discards
                        // MUI's built-in one; we already own the popup `open`
                        // state so the toggle just flips it. The popover still
                        // anchors correctly because the picker merges its
                        // `InputProps.ref` ahead of ours (only `endAdornment` is
                        // overridden, `ref` is preserved).
                        endAdornment: (
                            <InputAdornment position="end">
                                {clearable && !disabled ? (
                                    <IconButton
                                        // Match this app's Autocomplete clear
                                        // indicator: a default-size (medium)
                                        // IconButton with base padding:4 plus the
                                        // theme override in custom-theme-provider
                                        // (`MuiAutocomplete.clearIndicator`:
                                        // marginRight 6, marginBottom 2), and
                                        // ClearIcon fontSize "small".
                                        aria-label="Limpiar"
                                        title="Limpiar"
                                        // Like the Autocomplete clear indicator,
                                        // keep the icon out of the tab order — the
                                        // input is the single tab stop, and the
                                        // keyboard reaches clearing via Delete /
                                        // Backspace on the input (see onKeyDown).
                                        tabIndex={-1}
                                        sx={{
                                            padding: '4px',
                                            marginRight: '6px',
                                            marginBottom: '2px',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onChange(null);
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ) : null}
                                <IconButton
                                    edge="end"
                                    aria-label="Abrir calendario"
                                    disabled={disabled}
                                    // Out of the tab order too (Autocomplete
                                    // pattern); the popup opens from the input via
                                    // ArrowUp/ArrowDown/Enter/Space (see onKeyDown)
                                    // or by clicking the field.
                                    tabIndex={-1}
                                    // Medium size to match the clear button and
                                    // MUI's original open-picker button; baseline
                                    // aligned with the clear (marginBottom 2).
                                    sx={{ marginBottom: '2px' }}
                                    onClick={() => {
                                        setReferenceDate(dayjs());
                                        setOpen((prev) => !prev);
                                    }}
                                >
                                    <CalendarIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                    inputProps: {
                        // Truly read-only input: no caret. Preventing the
                        // mousedown default stops the multi-section field from
                        // focusing/highlighting a section on click (the "selects
                        // the text" weirdness) — clicking the input just opens
                        // the popup. Scoped to the input so the ✕ / calendar
                        // icon buttons keep working.
                        readOnly: true,
                        onMouseDown: (e: React.MouseEvent) => {
                            e.preventDefault();
                            if (!disabled) {
                                setReferenceDate(dayjs());
                                setOpen(true);
                            }
                        },
                        // Keyboard entry point (the input is the only tab stop).
                        // We handle keys in the CAPTURE phase so we run before
                        // the field's own key handler (`handleContainerKeyDown`):
                        //   - MUI would otherwise navigate sections on
                        //     ArrowLeft/ArrowRight ("moving through the date
                        //     text") even while readOnly, and let Enter bubble to
                        //     a native form submit.
                        //   - `stopPropagation()` in capture prevents that handler
                        //     from running at all; `preventDefault()` stops the
                        //     Enter form submit.
                        // Any arrow / Enter / Space opens the popup; Delete /
                        // Backspace clear the value (keyboard equivalent of the ✕,
                        // which is out of the tab order).
                        onKeyDownCapture: (e: React.KeyboardEvent) => {
                            if (disabled) {
                                return;
                            }
                            const isOpenKey =
                                e.key === 'ArrowDown' ||
                                e.key === 'ArrowUp' ||
                                e.key === 'ArrowLeft' ||
                                e.key === 'ArrowRight' ||
                                e.key === 'Enter' ||
                                e.key === ' ';
                            const isClearKey =
                                clearable &&
                                (e.key === 'Delete' || e.key === 'Backspace');
                            if (isOpenKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                setReferenceDate(dayjs());
                                setOpen(true);
                            } else if (isClearKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(null);
                            }
                        },
                    },
                    sx: {
                        my: !disableGutters ? 2 : 0,
                        width: '100%',
                        cursor: disabled ? 'default' : 'pointer',
                        '& .MuiInputBase-input': {
                            cursor: disabled ? 'default' : 'pointer',
                        },
                    },
                },
            }}
        />
    );
}
